<template>
  <div class="w-full h-full">
    <canvas ref="chartRef"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import Chart from 'chart.js/auto';

export default {
  name: 'TransactionYieldBandChart',
  props: {
    transactions: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const chartRef = ref(null);
    let chart = null;

    const processData = transactions => {
      // 날짜별로 거래를 그룹화
      const groupedByDate = transactions.reduce((acc, tx) => {
        const date = tx.transactionDateTime.split('T')[0];
        if (!acc[date]) {
          acc[date] = {
            yields: [],
            details: {}, // 최고/최저 금리 채권 정보를 저장
          };
        }
        const tradingYield = parseFloat(tx.tradingYield);
        acc[date].yields.push(tradingYield);

        // 해당 날짜의 최고/최저 금리 정보 업데이트
        if (
          !acc[date].details.max ||
          tradingYield > acc[date].details.max.tradingYield
        ) {
          acc[date].details.max = {
            tradingYield,
            bondName: tx.originalBondName,
            maturityDate: tx.maturityDate,
          };
        }
        if (
          !acc[date].details.min ||
          tradingYield < acc[date].details.min.tradingYield
        ) {
          acc[date].details.min = {
            tradingYield,
            bondName: tx.originalBondName,
            maturityDate: tx.maturityDate,
          };
        }
        return acc;
      }, {});

      // 각 날짜별 최대, 최소, 평균 금리 계산
      const dates = Object.keys(groupedByDate).sort();
      const minYields = [];
      const maxYields = [];
      const avgYields = [];
      const details = []; // 각 날짜의 상세 정보 저장

      dates.forEach(date => {
        const { yields, details: dateDetails } = groupedByDate[date];

        minYields.push(Math.min(...yields));
        maxYields.push(Math.max(...yields));
        avgYields.push(yields.reduce((a, b) => a + b, 0) / yields.length);
        details.push(dateDetails);
      });

      return {
        dates,
        minYields,
        maxYields,
        avgYields,
        details,
      };
    };

    const createChart = data => {
      const ctx = chartRef.value.getContext('2d');

      if (chart) {
        chart.destroy();
      }

      chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.dates,
          datasets: [
            {
              label: '평균 금리',
              data: data.avgYields,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1,
              fill: false,
            },
            {
              label: '최고 금리',
              data: data.maxYields,
              borderColor: 'rgba(75, 192, 192, 0.2)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              fill: 1,
              tension: 0.1,
              details: data.details.map(d => d.max),
            },
            {
              label: '최저 금리',
              data: data.minYields,
              borderColor: 'rgba(75, 192, 192, 0.2)',
              fill: false,
              tension: 0.1,
              showLine: true,
              details: data.details.map(d => d.min),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          //   plugins: {
          //     title: {
          //       display: true,
          //       text: '일자별 체결금리 밴드',
          //     },
          //   },
          scales: {
            y: {
              title: {
                display: true,
                text: '체결금리 (%)',
              },
              ticks: {
                callback: function (value) {
                  return value.toFixed(2);
                },
                stepSize: 0.02,
              },
              grid: {
                display: true,
                drawOnChartArea: true,
                color: 'rgba(0, 0, 0, 0.1)',
              },
            },
            x: {
              title: {
                display: true,
                text: '거래일자',
              },
              grid: {
                display: false,
              },
            },
          },
          elements: {
            point: {
              radius: 3,
              hoverRadius: 5,
            },
            line: {
              tension: 0.2,
            },
          },
          plugins: {
            tooltip: {
              displayColors: false,
              padding: 10,
              bodySpacing: 4,
              callbacks: {
                beforeLabel: function (context) {
                  let label = `${context.dataset.label}: ${Number(
                    context.raw
                  ).toFixed(2)}%`;
                  return label;
                },
                label: function (context) {
                  // 최�/최저 금리 채권 정보 추가
                  if (
                    context.dataset.details &&
                    context.dataset.details[context.dataIndex]
                  ) {
                    const detail = context.dataset.details[context.dataIndex];
                    return [
                      `종목명: ${detail.bondName}`,
                      `만기일: ${detail.maturityDate}`,
                    ];
                  }
                  return null;
                },
              },
            },
          },
        },
      });
    };

    watch(
      () => props.transactions,
      newTransactions => {
        if (newTransactions.length > 0) {
          const data = processData(newTransactions);
          createChart(data);
        }
      },
      { deep: true }
    );

    onMounted(() => {
      if (props.transactions.length > 0) {
        const data = processData(props.transactions);
        createChart(data);
      }
    });

    return {
      chartRef,
    };
  },
};
</script>
